// @ts-check

export const STREAM_ACTIVITY_KIND_REASONING = "reasoning"
export const STREAM_ACTIVITY_KIND_TOOL = "tool"

/**
 * @param {unknown} label
 * @returns {label is string}
 */
export function isValidStreamLabel(label) {
  return typeof label === "string" && label.trim().length > 0
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
export function completeActiveSegments(segments) {
  const list = segments ?? []
  return list.map((s) =>
    s.status === "active" ? { ...s, status: "completed" } : s
  )
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @param {import("./estimationAgent.types").StreamActivitySegment["kind"]} kind
 * @param {{ id: string; label: string }} payload
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
function applySegmentStart(segments, kind, payload) {
  const { id, label } = payload
  if (typeof id !== "string" || !isValidStreamLabel(label)) {
    return segments ?? []
  }
  const list = completeActiveSegments(segments)
  const trimmed = label.trim()
  const idx = list.findIndex((s) => s.id === id)
  const next = /** @type {import("./estimationAgent.types").StreamActivitySegment} */ ({
    id,
    kind,
    status: "active",
    label: trimmed,
  })
  if (idx >= 0) {
    const copy = [...list]
    copy[idx] = { ...copy[idx], ...next, status: "active" }
    return copy
  }
  return [...list, next]
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @param {{ id: string; label: string }} payload
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
export function applyReasoningStart(segments, payload) {
  return applySegmentStart(segments, STREAM_ACTIVITY_KIND_REASONING, payload)
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @param {{ id: string; label: string }} payload
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
export function applyToolStart(segments, payload) {
  return applySegmentStart(segments, STREAM_ACTIVITY_KIND_TOOL, payload)
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @param {import("./estimationAgent.types").StreamActivitySegment["kind"]} expectedKind
 * @param {{ id: string; label: string }} payload
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
function applySegmentEnd(segments, expectedKind, payload) {
  const { id, label } = payload
  if (typeof id !== "string" || !isValidStreamLabel(label)) {
    return segments ?? []
  }
  const list = segments ?? []
  const idx = list.findIndex((s) => s.id === id && s.kind === expectedKind)
  if (idx < 0) return list
  const trimmed = label.trim()
  const copy = [...list]
  copy[idx] = {
    ...copy[idx],
    status: "completed",
    label: trimmed,
  }
  return copy
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @param {{ id: string; label: string }} payload
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
export function applyReasoningEnd(segments, payload) {
  return applySegmentEnd(segments, STREAM_ACTIVITY_KIND_REASONING, payload)
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @param {{ id: string; label: string }} payload
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
export function applyToolEnd(segments, payload) {
  return applySegmentEnd(segments, STREAM_ACTIVITY_KIND_TOOL, payload)
}

/**
 * @param {import("./estimationAgent.types").StreamActivitySegment[] | undefined} segments
 * @returns {import("./estimationAgent.types").StreamActivitySegment[]}
 */
export function finalizeActivitySegments(segments) {
  const list = segments ?? []
  if (list.length === 0) return list
  return list.map((s) =>
    s.status === "active" ? { ...s, status: "completed" } : s
  )
}
